import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Grid, Typography, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SyncIcon from '@mui/icons-material/Sync';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import PercentIcon from '@mui/icons-material/Percent';
import { showModal } from 'react-redux-modal-provider';
import ConfirmationDialog from '../../modals/default/ConfirmationDialog';
import { ButtonBasic } from '../../components';
import PanelToolService from '../../services/panelTool.service';

const KEY_TO_AUXDATA = {
    nubicom: 'sync_force_nubicom',
    strong: 'sync_force_strong',
    softland: 'sync_force_softland',
    lugares: 'sync_force_lugares_calendario',
};

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ACTIONS = [
    {
        key: 'nubicom',
        label: 'Sincronizar usuarios Nubicom',
        description: 'Actualiza los usuarios desde Workie Nubicom hacia la base de datos local.',
        icon: <PeopleAltIcon sx={{ fontSize: 40 }} />,
        color: '#0364ff',
        confirmTitle: 'Sincronizar usuarios Nubicom',
        confirmMessage: '¿Desea sincronizar los usuarios de Nubicom? Esto puede tardar unos segundos.',
        serviceFn: () => PanelToolService.syncNubicom(),
    },
    {
        key: 'strong',
        label: 'Sincronizar usuarios Strong',
        description: 'Actualiza los usuarios desde Workie Strong hacia la base de datos local.',
        icon: <PeopleAltIcon sx={{ fontSize: 40 }} />,
        color: '#7c3aed',
        confirmTitle: 'Sincronizar usuarios Strong',
        confirmMessage: '¿Desea sincronizar los usuarios de Strong? Esto puede tardar unos segundos.',
        serviceFn: () => PanelToolService.syncStrong(),
    },
    {
        key: 'softland',
        label: 'Sincronizar Data Softland',
        description: 'Actualiza catálogos de Softland: países, provincias, zonas, productos, contratos y más.',
        icon: <BusinessIcon sx={{ fontSize: 40 }} />,
        color: '#059669',
        confirmTitle: 'Sincronizar Data Softland',
        confirmMessage: '¿Desea sincronizar los datos de Softland? Esto puede tardar unos segundos.',
        serviceFn: () => PanelToolService.syncSoftland(),
    },
    {
        key: 'lugares',
        label: 'Sincronizar lugares Calendario',
        description: 'Actualiza la lista de lugares disponibles en el calendario de tareas.',
        icon: <PlaceIcon sx={{ fontSize: 40 }} />,
        color: '#d97706',
        confirmTitle: 'Sincronizar lugares Calendario',
        confirmMessage: '¿Desea sincronizar los lugares del calendario?',
        serviceFn: () => PanelToolService.syncLugares(),
    },
];

const PanelToolScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [loadingKey, setLoadingKey] = useState(null);
    const [porcentajeDA, setPorcentajeDAState] = useState(null);
    const [loadingDA, setLoadingDA] = useState(false);
    const [lastSyncs, setLastSyncs] = useState({});

    const loadInitialData = () => {
        PanelToolService.getLastSyncs()
            .then((resp) => {
                setLastSyncs(resp || {});
                setPorcentajeDAState(resp?.porcentaje_da_value || null);
            })
            .catch((e) => console.log(e));
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    const onClickAction = (action) => {
        showModal(ConfirmationDialog, {
            title: action.confirmTitle,
            message: action.confirmMessage,
            onConfirm: async () => {
                setLoadingKey(action.key);
                try {
                    await action.serviceFn();
                    PanelToolService.getLastSyncs()
                        .then((resp) => setLastSyncs(resp || {}))
                        .catch(() => {});
                } catch (e) {
                    console.log(e);
                } finally {
                    setLoadingKey(null);
                }
            },
        });
    };

    const onChangePorcentajeDA = (event, newValue) => {
        if (!newValue || newValue === porcentajeDA) return;
        showModal(ConfirmationDialog, {
            title: 'Cambiar Porcentaje DA',
            message: `¿Desea cambiar el porcentaje DA a ${newValue}%?`,
            onConfirm: async () => {
                setLoadingDA(true);
                try {
                    await PanelToolService.setPorcentajeDA(newValue);
                    setPorcentajeDAState(newValue);
                    PanelToolService.getLastSyncs()
                        .then((resp) => {
                            setLastSyncs(resp || {});
                            setPorcentajeDAState(resp?.porcentaje_da_value || null);
                        })
                        .catch(() => {});
                } catch (e) {
                    console.log(e);
                } finally {
                    setLoadingDA(false);
                }
            },
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 'bold',
                    mb: 3,
                    color: isDarkMode ? '#e0e0e0' : '#15294b',
                }}
            >
                Panel de Herramientas
            </Typography>
            <Grid container spacing={3}>
                {ACTIONS.map((action) => (
                    <Grid item xs={12} sm={6} md={3} key={action.key}>
                        <Card
                            sx={{
                                background: isDarkMode ? '#1F2A40' : '#fff',
                                border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                                borderRadius: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <CardContent
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexGrow: 1,
                                    pb: 1,
                                }}
                            >
                                <Box sx={{ color: action.color }}>
                                    {action.icon}
                                </Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        color: isDarkMode ? '#e0e0e0' : '#15294b',
                                    }}
                                >
                                    {action.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: 'center',
                                        color: isDarkMode ? '#a0aec0' : '#6b7280',
                                        flexGrow: 1,
                                    }}
                                >
                                    {action.description}
                                </Typography>
                                {lastSyncs[KEY_TO_AUXDATA[action.key]] && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: isDarkMode ? '#6b7280' : '#9ca3af', textAlign: 'center' }}
                                    >
                                        Última vez: {formatDate(lastSyncs[KEY_TO_AUXDATA[action.key]])}
                                    </Typography>
                                )}
                                <ButtonBasic
                                    textButton={
                                        loadingKey === action.key ? (
                                            <CircularProgress size={18} sx={{ color: '#fff' }} />
                                        ) : (
                                            <>
                                                <SyncIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                Sincronizar
                                            </>
                                        )
                                    }
                                    variant="wk"
                                    color="nextWhite"
                                    sx={{ border: 1, borderColor: action.color, mt: 1, width: '100%' }}
                                    onClick={() => onClickAction(action)}
                                    isLoading={loadingKey === action.key}
                                    disabled={loadingKey !== null}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Card Porcentaje DA */}
            <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card
                        sx={{
                            background: isDarkMode ? '#1F2A40' : '#fff',
                            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
                            borderRadius: 2,
                        }}
                    >
                        <CardContent
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ color: '#e11d48' }}>
                                <PercentIcon sx={{ fontSize: 40 }} />
                            </Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    color: isDarkMode ? '#e0e0e0' : '#15294b',
                                }}
                            >
                                Porcentaje DA
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: 'center',
                                    color: isDarkMode ? '#a0aec0' : '#6b7280',
                                }}
                            >
                                Habilita la bonificación de débito automático al 10% o 20%.
                            </Typography>
                            {lastSyncs['porcentaje_da'] && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: isDarkMode ? '#6b7280' : '#9ca3af', textAlign: 'center' }}
                                >
                                    Último cambio: {formatDate(lastSyncs['porcentaje_da'])}
                                </Typography>
                            )}
                            {loadingDA ? (
                                <CircularProgress size={24} sx={{ mt: 1 }} />
                            ) : (
                                <ToggleButtonGroup
                                    value={porcentajeDA}
                                    exclusive
                                    onChange={onChangePorcentajeDA}
                                    sx={{ mt: 1 }}
                                    disabled={loadingDA}
                                >
                                    <ToggleButton value="10" sx={{ px: 3, fontWeight: 'bold' }}>
                                        10%
                                    </ToggleButton>
                                    <ToggleButton value="20" sx={{ px: 3, fontWeight: 'bold' }}>
                                        20%
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PanelToolScreen;
